<template>
  <DetailsLayout
    type="transaction"
    :date="header?.date"
    :copyValue="header?.copyValue"
    :rate="network === 'bitcoin' ? BTC_Rate : TRON_Rate"
    :error="error"
  >
    <template #left>
      <TxnStatusBanner v-if="isMobile" :status="mainInfo?.status" />
      <MainInfoInstantLink
        :icon="mainInfo?.icon"
        :details="mainInfo?.details"
        :amountData="mainInfo?.amountData"
        :loading="isLoading"
      />
    </template>
    <template #right>
      <TxnStatusBanner v-if="!isMobile" :status="mainInfo?.status" />
      <TransactionDetailInstantLink />
      <InstantLinkBtcDetails
        v-if="network === 'bitcoin'"
        :input="input"
        :output="output"
        :network="network"
        :isLoading="isLoading"
      />
      <div class="transaction-block">
        <AddressBlockTron
          v-if="network === 'tron'"
          :addressFrom="input?.address"
          :addressTo="output?.address"
          :amount="input?.amount"
          :decimals="input?.decimals"
          :symbol="input?.symbol"
          :usd_value="input?.usd_value"
          :network="network"
          :showArrow="false"
          :amountTransferred="input"
          :isLoading="false"
          :isInstantLink="true"
        />
      </div>
    </template>
  </DetailsLayout>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { DetailsLayout } from "@/widgets";
import { MainInfoInstantLink } from "@/features/MainInfoInstantLink";
import TransactionDetailInstantLink from "@/features/TransactionDetails/Common/ui/TransactionDetailInstantLink.vue";
import InstantLinkBtcDetails from "@/features/TransactionDetails/Bitcoin/ui/InstantLinkBtcDetails.vue";
import AddressBlockTron from "@/features/TransactionDetails/Common/ui/AddressBlockTron.vue";
import TxnStatusBanner from "@/shared/ui/TxnStatusBanner/TxnStatusBanner.vue";
import { useBreakpoints } from "@/shared/utils/ui/useBreakpoints";
import {
  getSymbol,
  formatFiat,
} from "@/shared/utils/formaters/transactionFormatter";
import {
  formatDateMonthInFront,
  capitalizeFirstLetter,
} from "@/shared/utils/format/date";
import { capitalize } from "@/shared/utils/format/string";

// Store и route
const store = useStore();
const route = useRoute();
const router = useRouter();
const { locale } = useI18n();
const { isMobile } = useBreakpoints();

// Reactive data
const isLoading = ref(false);
const error = ref("");
const data = ref(null);
const input = ref(null);
const output = ref(null);

// Computed properties
const ratePairs = computed(() => store.state.coinService.ratePairs);

const header = computed(() => data.value?.header);

const BTC_Rate = computed(() => {
  const btcValue = ratePairs.value["btc_usd"] || 0;
  return {
    value: btcValue,
    symbol: "BTC",
  };
});

const TRON_Rate = computed(() => {
  const tronValue = ratePairs.value["trx_usd"] || 0;
  return {
    value: tronValue,
    symbol: "TRX",
  };
});

const mainInfo = computed(() => data.value?.mainInfo);

const network = computed(() => route.params.network);

const getUserData = computed(() => store.getters["user/getUserData"]);

// Methods
const GET_TRANSACTION = (params) =>
  store.dispatch("explorer/GET_INSTANT_TRANSACTION", params);

// Function to update document title dynamically
const updateDocumentTitle = (transactionData) => {
  if (transactionData) {
    const network =
      route.params.network === "bitcoin"
        ? capitalize(route.params.network || "")
        : route.params.network.toUpperCase();

    document.title = `${network} Txn - EMCD explorer`;
  }
};

let pollTimer = null;
let pollingStopped = false;

const getNetworkFromId = (network) => {
  if (network === "btc") {
    return "bitcoin";
  }
  if (network === "trc20") {
    return "tron";
  }
  return network;
};

const getIconFromToken = (token) => {
  if (token === "btc") {
    return "bitcoin";
  }
  if (token === "trx") {
    return "tron";
  }
  return token; //add usdt later
};

const getStatus = (status) => {
  if (status === "failed") {
    return "failed";
  }
  return "pending";
};

const fetchInstantTransaction = async () => {
  isLoading.value = true;
  try {
    const response = await GET_TRANSACTION({
      payload: route.params.payload,
    });

    data.value = {
      mainInfo: {
        amountData: {
          amount: `${response?.amount} ${getSymbol(response?.token)}`,
          usd_value: formatFiat(response?.amount_usd),
        },
        details: [
          {
            id: 1,
            label: "Coin",
            value: `${capitalizeFirstLetter(response?.coin?.description)} • ${getSymbol(
              response?.token,
            )}`,
          },
          {
            id: 2,
            label: "Time",
            value: formatDateMonthInFront(response?.created_at, locale.value),
          },
          {
            id: 3,
            label: "Hash",
            value: "",
            underline: false,
          },
        ],
        icon: getIconFromToken(response?.token),
        status: getStatus(response?.status),
      },
      header: {
        date: response?.created_at,
      },
    };

    input.value = {
      amount: response.amount,
      decimals: 0,
      usd_value: response.amount_usd ?? 0,
      formattedFiat: `$\u202F${response.usd_value}`,
      address: "",
      symbol: getSymbol(response.token),
    };

    output.value = {
      amount: response.amount,
      decimals: 0,
      usd_value: response.amount_usd ?? 0,
      formattedFiat: `$\u202F${response.usd_value}`,
      address: response.to_address,
      symbol: getSymbol(response.token),
    };

    // Update document title with transaction data
    updateDocumentTitle(response);

    // Условия остановки/редиректа
    if (response?.tx_hash && String(response.tx_hash).trim() !== "") {
      pollingStopped = true;
      router.push(
        `/${getNetworkFromId(response?.network)}/transaction/${response?.tx_hash}`,
      );
      return;
    }

    if (response?.status === "failed") {
      pollingStopped = true;
    }
  } catch (err) {
    console.error("🔍 TransactionPage - Error loading transaction:", err);
  } finally {
    isLoading.value = false;
    if (!pollingStopped) {
      pollTimer = setTimeout(fetchInstantTransaction, 3000);
    }
  }
};

// Lifecycle
onMounted(() => {
  if (!["tron", "bitcoin"].includes(route.params.network)) {
    router.push("/404");
    return;
  }
  fetchInstantTransaction();

  window.emcd.AnalyticsService.track("page_view", {
    event_type: "page_view",
    product: "explorer",
    screen: "instant_transaction",
    type: getUserData.value ? "internal_tx_view" : "external_tx_view",
    user_id: getUserData.value ? getUserData.value.userData.user_id : null,
    utm_source: route.query.utm_source || null,
    utm_medium: route.query.utm_medium || null,
    utm_campaign: route.query.utm_campaign || null,
    referrer: route.query.referrer || null,
  });
});

onUnmounted(() => {
  if (pollTimer) {
    clearTimeout(pollTimer);
  }
});
</script>

<style scoped lang="scss">
.transaction-block {
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--Dark-850, #1a1a1a);
  background: var(--Dark-900, #111);
  padding: 32px;
}
</style>
